import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Eye, EyeOff, FileText, Image as ImageIcon, File as FileIcon, Plus } from "lucide-react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import UploadDocumentModal from "./UploadDocumentModal";

const TYPE_LABEL = { Contratto: "Contratto", Logo: "Logo", Altro: "Altro" };

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return "—"; }
}

function typeIcon(t) {
    if (t === "Contratto") return <FileText size={13} strokeWidth={2} />;
    if (t === "Logo") return <ImageIcon size={13} strokeWidth={2} />;
    return <FileIcon size={13} strokeWidth={2} />;
}

function TypeChip({ type }) {
    const cls = type === "Contratto" ? "lt-chip-doc--contratto"
        : type === "Logo" ? "lt-chip-doc--logo"
        : "lt-chip-doc--altro";
    return (
        <span className={`lt-chip-doc ${cls}`}>
            {typeIcon(type)} {TYPE_LABEL[type] || type}
        </span>
    );
}

function VisibilityBadge({ visible }) {
    if (visible) return (
        <span className="lt-visibility lt-visibility--public">
            <Eye size={12} strokeWidth={2} /> Visibile al cliente
        </span>
    );
    return (
        <span className="lt-visibility lt-visibility--internal">
            <EyeOff size={12} strokeWidth={2} /> Interno
        </span>
    );
}

async function streamDownload(id, fallbackName) {
    try {
        const res = await fetch(`${API}/internal/documenti/${id}/download`, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const cd = res.headers.get("Content-Disposition") || "";
        let filename = fallbackName || "documento";
        const m1 = cd.match(/filename\*=UTF-8''([^;]+)/i);
        const m2 = cd.match(/filename="([^"]+)"/i);
        if (m1) filename = decodeURIComponent(m1[1]);
        else if (m2) filename = m2[1];
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
        toast.error("Download fallito");
    }
}

/**
 * Vista di proiezione dei documenti del cliente nella scheda dettaglio.
 * Riusa GET /internal/documenti?client_id=... e /internal/documenti/{id}/download.
 * Nessun endpoint parallelo, nessuna duplicazione di stato.
 */
export default function ClientDocumentsPanel({ clientId, clientName }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [uploadOpen, setUploadOpen] = useState(false);

    const load = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const { data } = await api.get("/internal/documenti", {
                params: { client_id: clientId, page_size: 100 },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch {
            toast.error("Errore nel caricamento documenti");
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="lt-card" style={{ padding: "28px", marginTop: "24px" }} data-testid="client-documents-panel">
            <div className="flex items-center justify-between" style={{ marginBottom: "14px", gap: "16px", flexWrap: "wrap" }}>
                <div>
                    <div className="lt-eyebrow-mono" style={{ marginBottom: "6px" }}>DOCUMENTI</div>
                    <h2 className="lt-section-title" style={{ margin: 0 }} data-testid="client-documents-heading">
                        Documenti di {clientName}
                    </h2>
                    <p className="lt-helper" style={{ marginTop: "6px" }} data-testid="client-documents-count">
                        {loading ? "Caricamento…" : `${total} ${total === 1 ? "documento" : "documenti"}`}
                    </p>
                </div>
                <div className="flex items-center gap-3" style={{ flexWrap: "wrap" }}>
                    <Link
                        to={`/app/documenti?client_id=${clientId}`}
                        className="lt-btn-secondary"
                        data-testid="client-documents-view-all"
                        style={{ textDecoration: "none" }}
                    >
                        Apri modulo Documenti
                    </Link>
                    <button
                        type="button"
                        className="lt-btn-primary"
                        onClick={() => setUploadOpen(true)}
                        data-testid="client-documents-upload-btn"
                        style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                    >
                        <Plus size={14} strokeWidth={2.25} /> Carica documento
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={{ padding: "24px", textAlign: "center", color: "#8A8F88", fontSize: "14px" }}>
                    Caricamento…
                </div>
            ) : items.length === 0 ? (
                <div style={{ padding: "28px 20px", textAlign: "center", background: "#F8F8F5", border: "1px dashed #D6D8D2", borderRadius: "11px" }} data-testid="client-documents-empty">
                    <p style={{ color: "#5F6560", margin: "0 0 12px 0", fontSize: "14px" }}>
                        Nessun documento caricato per questo cliente.
                    </p>
                    <button
                        type="button"
                        className="lt-btn-primary"
                        onClick={() => setUploadOpen(true)}
                        data-testid="client-documents-empty-cta"
                        style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                    >
                        <Plus size={14} strokeWidth={2.25} /> Carica documento
                    </button>
                </div>
            ) : (
                <div style={{ maxHeight: "360px", overflowY: "auto", borderTop: "1px solid #EEEFEA" }} data-testid="client-documents-list">
                    {items.map((d) => (
                        <div
                            key={d.id}
                            data-testid={`client-doc-row-${d.id}`}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr auto",
                                gap: "16px",
                                alignItems: "center",
                                padding: "14px 4px",
                                borderBottom: "1px solid #EEEFEA",
                            }}
                        >
                            <div style={{ minWidth: 0 }}>
                                <div style={{ color: "#181B20", fontSize: "14px", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {d.name}
                                </div>
                                <div className="flex items-center gap-3" style={{ marginTop: "6px", flexWrap: "wrap" }}>
                                    <TypeChip type={d.type} />
                                    <VisibilityBadge visible={d.visible_to_client} />
                                    <span style={{ color: "#8A8F89", fontSize: "12px", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "0.03em" }}>
                                        {fmtDate(d.created_at)}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="lt-btn-ghost"
                                onClick={() => streamDownload(d.id, d.original_filename)}
                                data-testid={`client-doc-download-${d.id}`}
                                aria-label={`Scarica ${d.name}`}
                                style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                            >
                                <Download size={14} strokeWidth={2} /> Scarica
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <UploadDocumentModal
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                clientId={clientId}
                clientName={clientName}
                onUploaded={() => { load(); }}
            />
        </div>
    );
}
